import React, { useState } from "react";
import { api, setAuth } from "@/lib/api";
import { Logo } from "@/components/Header";

export default function AuthPage({ onAuthed }) {
  const [mode, setMode] = useState("login"); // 'login' | 'register'
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const url = mode === "login" ? "/auth/login" : "/auth/register";
      const body = mode === "login" ? { email, password } : { email, password, name };
      const { data } = await api.post(url, body);
      setAuth(data.token, data.user);
      onAuthed(data.user);
    } catch (e) {
      setErr(e?.response?.data?.detail?.toString?.() || "Something went wrong.");
    } finally { setBusy(false); }
  }

  // Submit button label — flattened from a nested ternary for clarity.
  let submitLabel = mode === "login" ? "Enter →" : "Create →";
  if (busy) submitLabel = "…";

  return (
    <div className="min-h-screen blueprint-grid grain flex items-stretch">
      <div className="flex-1 hidden lg:flex flex-col justify-between border-r hairline p-12 relative">
        <div className="flex items-center gap-3">
          <Logo />
          <div>
            <div className="font-serif-display text-2xl italic leading-none">
              VeraLok <span className="brand-gradient">Neuralcraft</span>
            </div>
            <div className="label-xs">operator console</div>
          </div>
        </div>
        <div>
          <h1 className="font-serif-display text-6xl xl:text-7xl leading-[0.95] tracking-tighter">
            A living<br/>blueprint <span className="italic">for</span><br/>
            <span className="italic brand-gradient">intelligence.</span>
          </h1>
          <p className="mt-8 max-w-md text-[15px] leading-relaxed text-[#A8B3C7]">
            Eleven specialised agents arranged across three functional layers,
            held together by shared memory and a closed feedback loop. Give it a goal &mdash;
            watch the organism think.
          </p>
        </div>
        <div className="grid grid-cols-3 gap-0 border-t border-l hairline">
          {["Orchestrator","Brain","Hands","Legs","Organs","Feedback"].map((l,i) => (
            <div key={l} className="border-r border-b hairline px-4 py-3">
              <div className="label-xs">Layer · {String(i).padStart(2,"0")}</div>
              <div className="font-mono text-sm mt-1">{l}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-md border hairline bg-[#071a35]/70 backdrop-blur p-10" data-testid="auth-form">
          <div className="label-xs mb-2">{mode === "login" ? "Sign in" : "Create account"} · 01</div>
          <h2 className="font-serif-display text-4xl italic leading-tight mb-8">
            {mode === "login" ? "Welcome back." : "Begin here."}
          </h2>

          {mode === "register" && (
            <label className="block mb-6">
              <span className="label-xs">Name</span>
              <input
                className="input-line"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ada Lovelace"
                required
                data-testid="auth-name-input"
              />
            </label>
          )}

          <label className="block mb-6">
            <span className="label-xs">Email</span>
            <input
              type="email"
              className="input-line"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@studio.com"
              required
              data-testid="auth-email-input"
            />
          </label>

          <label className="block mb-8">
            <span className="label-xs">Password</span>
            <input
              type="password"
              className="input-line"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              minLength={6}
              required
              data-testid="auth-password-input"
            />
          </label>

          {err && (
            <div className="mb-6 border-l-2 border-[#FF5B5B] pl-3 py-1 font-mono text-xs text-[#FF5B5B]" data-testid="auth-error">
              {err}
            </div>
          )}

          <div className="flex items-center justify-between gap-4">
            <button type="submit" disabled={busy} className="btn-primary" data-testid="auth-submit-btn">
              {submitLabel}
            </button>
            <button
              type="button"
              onClick={() => { setErr(""); setMode(mode === "login" ? "register" : "login"); }}
              className="font-mono text-xs uppercase tracking-[0.22em] underline underline-offset-4 hover:text-[#00B4E6]"
              data-testid="auth-toggle-btn"
            >
              {mode === "login" ? "No account? Register" : "Have one? Sign in"}
            </button>
          </div>

          <div className="mt-10 pt-6 border-t hairline label-xs">
            {mode === "login" ? "Operators sign in to dispatch goals." : "One operator, eleven agents, infinite runs."}
          </div>
        </form>
      </div>
    </div>
  );
}
